/* ==========================================================================
   VELORA — Theme scripts
   ========================================================================== */

(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const body = document.body;

  /* Scroll reveals ------------------------------------------------------- */
  function initReveals() {
    const els = document.querySelectorAll('.reveal, .reveal-line');
    if (!('IntersectionObserver' in window) || prefersReduced) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
    els.forEach(el => io.observe(el));
  }

  /* Parallax ------------------------------------------------------------- */
  function initParallax() {
    if (prefersReduced) return;
    const els = document.querySelectorAll('.parallax');
    if (!els.length) return;
    let ticking = false;

    function update() {
      const h = window.innerHeight;
      els.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > h) return;
        const speed = parseFloat(el.dataset.parallaxSpeed || 0.12);
        const center = rect.top + rect.height / 2 - h / 2;
        el.style.transform = `translate3d(0, ${-center * speed}px, 0)`;
      });
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* Header scrolled state ------------------------------------------------ */
  function initHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Mobile menu ---------------------------------------------------------- */
  function initMobileMenu() {
    const toggle = document.querySelector('[data-mobile-toggle]');
    const drawer = document.querySelector('[data-mobile-menu]');
    if (!toggle || !drawer) return;
    const close = drawer.querySelector('[data-mobile-close]');
    toggle.addEventListener('click', () => { drawer.classList.add('is-open'); body.style.overflow = 'hidden'; });
    if (close) close.addEventListener('click', () => { drawer.classList.remove('is-open'); body.style.overflow = ''; });
    drawer.querySelectorAll('a').forEach(a => a.addEventListener('click', () => {
      drawer.classList.remove('is-open'); body.style.overflow = '';
    }));
  }

  /* Cart drawer ---------------------------------------------------------- */
  async function openCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-overlay]');
    if (!drawer) return;
    try {
      const res = await fetch('/cart.js');
      const data = await res.json();
      renderCart(drawer, data);
    } catch (e) { console.warn('Cart fetch failed', e); }
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-overlay]');
    if (drawer) drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    body.style.overflow = '';
  }

  function renderCart(drawer, cart) {
    const body_ = drawer.querySelector('[data-cart-body]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    const footer = drawer.querySelector('[data-cart-footer]');
    if (!body_) return;
    if (!cart || cart.item_count === 0) {
      body_.innerHTML = `
        <div class="cart-empty">
          <p class="eyebrow">Your bag awaits</p>
          <h3 class="h3">Begin your ritual.</h3>
          <p class="lede">Your bag is empty. Start with our heat + cold sculpting tool.</p>
          <a class="btn mt-md" href="/collections/all">Shop now</a>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }
    if (footer) footer.style.display = '';
    const items = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__media">
          ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
        </div>
        <div>
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__meta">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
          <div class="cart-item__actions">
            <div class="mono">Qty ${item.quantity}</div>
            <button class="cart-item__remove" data-cart-remove="${item.key}">Remove</button>
          </div>
        </div>
      </div>
    `).join('');
    body_.innerHTML = items;
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    updateCartCount(cart.item_count);
    body_.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', () => removeFromCart(btn.dataset.cartRemove, drawer));
    });
  }

  async function removeFromCart(key, drawer) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      const data = await res.json();
      renderCart(drawer, data);
    } catch (e) { console.warn(e); }
  }

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch { return `$${amount}`; }
  }

  function updateCartCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
  }

  function initCart() {
    document.querySelectorAll('[data-cart-open]').forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault(); openCartDrawer();
    }));
    document.querySelectorAll('[data-cart-close]').forEach(btn => btn.addEventListener('click', closeCartDrawer));
    const overlay = document.querySelector('[data-overlay]');
    if (overlay) overlay.addEventListener('click', closeCartDrawer);
    // Intercept product forms to add via fetch
    document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        const orig = submit ? submit.innerHTML : '';
        if (submit) { submit.disabled = true; submit.innerHTML = 'Adding&hellip;'; }
        try {
          const fd = new FormData(form);
          const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
          if (!res.ok) throw new Error('Add to cart failed');
          await res.json();
          await openCartDrawer();
        } catch (err) {
          console.warn(err);
          window.location.href = '/cart';
        } finally {
          if (submit) { submit.disabled = false; submit.innerHTML = orig; }
        }
      });
    });
    // Fetch count on load
    fetch('/cart.js').then(r => r.json()).then(c => updateCartCount(c.item_count)).catch(() => {});
  }

  /* Cursor follower ------------------------------------------------------ */
  function initCursor() {
    if (body.classList.contains('no-cursor')) return;
    const cur = document.querySelector('.cursor-follower');
    if (!cur) return;
    let x = 0, y = 0, tx = 0, ty = 0, active = false;

    window.addEventListener('mousemove', (e) => {
      tx = e.clientX; ty = e.clientY;
      if (!active) { cur.classList.add('is-active'); active = true; }
    });
    window.addEventListener('mouseout', (e) => {
      if (!e.relatedTarget) { cur.classList.remove('is-active'); active = false; }
    });
    function loop() {
      x += (tx - x) * 0.18;
      y += (ty - y) * 0.18;
      cur.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      requestAnimationFrame(loop);
    }
    loop();
    document.querySelectorAll('a, button, [data-cursor-hover], .product-card, .editorial-grid__item').forEach(el => {
      el.addEventListener('mouseenter', () => cur.classList.add('is-hover'));
      el.addEventListener('mouseleave', () => cur.classList.remove('is-hover'));
    });
  }

  /* Accordion (native details) — no JS needed, but smooth */

  /* Product gallery ------------------------------------------------------ */
  function initGallery() {
    const galleries = document.querySelectorAll('[data-gallery]');
    galleries.forEach(g => {
      const main = g.querySelector('[data-gallery-main] img');
      const thumbs = g.querySelectorAll('[data-gallery-thumb]');
      thumbs.forEach(t => {
        t.addEventListener('click', () => {
          thumbs.forEach(x => x.classList.remove('is-active'));
          t.classList.add('is-active');
          if (main && t.dataset.src) {
            main.style.opacity = 0;
            const pre = new Image();
            pre.onload = () => { main.removeAttribute('srcset'); main.removeAttribute('sizes'); main.src = t.dataset.src; main.style.opacity = 1; };
            pre.src = t.dataset.src;
          }
        });
      });
    });
  }

  /* Product variant/options ---------------------------------------------- */
  function initVariants() {
    document.querySelectorAll('[data-product-form]').forEach(form => {
      const variantInput = form.querySelector('[data-variant-id]');
      const priceEl = form.querySelector('[data-price]');
      const compareEl = form.querySelector('[data-price-compare]');
      const submit = form.querySelector('[type="submit"]');
      const optionGroups = form.querySelectorAll('[data-option-group]');
      if (!variantInput) return;
      const variants = JSON.parse(form.dataset.variants || '[]');

      function pick() {
        const selected = [];
        optionGroups.forEach(g => {
          const active = g.querySelector('.is-active');
          selected.push(active ? active.dataset.value : null);
        });
        const match = optionGroups.length === 0 ? variants[0] : variants.find(v => v.options.every((o, i) => o === selected[i]));
        if (!match) {
          if (submit) { submit.disabled = true; submit.dataset.origLabel = submit.dataset.origLabel || submit.textContent; submit.textContent = 'Unavailable'; }
          return;
        }
        variantInput.value = match.id;
        if (priceEl) priceEl.textContent = match.price;
        if (compareEl) {
          if (match.compare_at_price && match.compare_at_price !== match.price) {
            compareEl.textContent = match.compare_at_price;
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
        if (submit) {
          submit.disabled = !match.available;
          submit.textContent = match.available ? (submit.dataset.origLabel || 'Add to cart') : 'Sold out';
          submit.dataset.origLabel = submit.dataset.origLabel || 'Add to cart';
        }
      }

      optionGroups.forEach(group => {
        group.querySelectorAll('[data-value]').forEach(el => {
          el.addEventListener('click', () => {
            group.querySelectorAll('[data-value]').forEach(x => x.classList.remove('is-active'));
            el.classList.add('is-active');
            pick();
          });
        });
      });

      const qtyEl = form.querySelector('[data-qty]');
      if (qtyEl) {
        const dec = form.querySelector('[data-qty-dec]');
        const inc = form.querySelector('[data-qty-inc]');
        if (dec) dec.addEventListener('click', () => { qtyEl.value = Math.max(1, parseInt(qtyEl.value || '1') - 1); });
        if (inc) inc.addEventListener('click', () => { qtyEl.value = parseInt(qtyEl.value || '1') + 1; });
      }

      pick();
    });
  }

  /* Init ------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initReveals();
    initParallax();
    initMobileMenu();
    initCart();
    initCursor();
    initGallery();
    initVariants();
  });
})();
(function(){var el=document.getElementById('velora-countdown');if(!el)return;var target=new Date(el.dataset.target).getTime();if(isNaN(target))return;var tmpl=el.dataset.template;var fb=el.dataset.fallback;function p(n){return n<10?'0'+n:n;}function fmt(ms){var s=Math.floor(ms/1000);var d=Math.floor(s/86400);s-=d*86400;var h=Math.floor(s/3600);s-=h*3600;var m=Math.floor(s/60);s-=m*60;var a=[];if(d>0)a.push(d+'d');a.push(p(h)+'h');a.push(p(m)+'m');if(d===0)a.push(p(s)+'s');return a.join(' ');}function tick(){var r=target-Date.now();if(r<=0){el.textContent=fb;return;}el.textContent=tmpl.replace('{time}',fmt(r));}tick();setInterval(tick,1000);})();